import { FC, useMemo, useState } from 'react';
import { Box, Center, HStack } from '@chakra-ui/react';

import ErrorCard from 'components/ErrorCard';
import dashboardLocales from 'features/Dashboard/common/locales';
import * as ContentLayout from 'layouts/ContentLayout';

import Button from '../../../../components/_core/Button';
import Heading from '../../../../components/_core/Heading';
import { BarLineGraphData } from '../../types';
import BarLineGraph from '../components/BarLineGraph';
import { BetaTag } from '../components/BetaTag';
import { CashflowBankAccountPicker } from '../components/CashflowBankAccountPicker';
import { useCashflowBankAccountsContext } from '../components/CashflowBankAccountsContext';
import IntervalContextProvider from '../components/IntervalContext';
import RangeIntervalPicker from '../components/RangeIntervalPicker';
import {
  getDefaultYearlyValue,
  RangeIntervalDate,
} from '../components/RangeIntervalPicker/utils';
import {
  GraphEmptyState,
  UnavailableDateEmptyState,
} from '../components/TransactionAggregationTable/components/EmptyStates';
import useDashboardNeedsEmptyState from '../hooks/useDashboardNeedsEmptyState';
import { useDashboardQuery } from '../hooks/useDashboardQuery';
import useDashboardUnavailableInterval from '../hooks/useDashboardUnavailableInterval';
import LoadingTreasuryGraph from './components/LoadingTreasuryGraph';
import locales from './locales';

const Treasury: FC = () => {
  const [interval, setInterval] = useState<RangeIntervalDate>(
    getDefaultYearlyValue(),
  );

  const { selectedBankAccountId, setSelectedBankAccountId } =
    useCashflowBankAccountsContext();

  const needsUnavailableDateState = useDashboardUnavailableInterval(
    interval.currentInterval.from,
    interval.currentInterval.to,
  );

  const { balance, error, granularity, payin, payout } = useDashboardQuery({
    // We can assume `selectedBankAccountId` is not null because
    // this component is rendered under the `loader`
    // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
    bankAccountId: selectedBankAccountId!,
    endDate: interval.currentInterval.to,
    startDate: interval.currentInterval.from,
  });

  const needsEmptyState = useDashboardNeedsEmptyState({
    balance,
    payin,
    payout,
  });

  const graphData = useMemo(() => {
    if (!payin || !payout || !balance) {
      return [];
    }

    const data: BarLineGraphData[] = [];

    for (let i = 0; i < payin.length; i++) {
      if (!payin[i + 1] || !payout[i + 1]) {
        break;
      }
      // The total payin and payout for a specific period is processed in the next point
      // Ex: The payin and payout for January would be at February 1st
      // @ts-expect-error Object is possibly 'undefined'.
      const currentPayin = payin[i + 1].newValue;
      // @ts-expect-error Object is possibly 'undefined'.
      const currentPayout = payout[i + 1].newValue;

      // @ts-expect-error Object is possibly 'undefined'.
      const currentDate = balance[i].newDate;
      // The balance at the start of the date
      // @ts-expect-error Object is possibly 'undefined'.
      const currentBalance = balance[i].newValue;

      // Hide future points
      if (currentDate && new Date(currentDate) > new Date()) {
        data.push({
          date: currentDate,
          earnings: null,
          fullData: {
            earnings: null,
            monthStartBalance: null,
            spendings: null,
          },
          monthStartBalance: null,
          spendings: null,
        });
      } else {
        // We create 2 data-points to allow us to offset the
        // balance chart from the earnings/spendings bars.
        // -> Allows the bars to be set in between 2 balance dots
        data.push(
          {
            date: currentDate || '',
            earnings: null,
            fullData: {
              earnings: currentPayin,
              monthStartBalance: currentBalance,
              spendings: currentPayout,
            },
            monthStartBalance: currentBalance,
            spendings: null,
          },
          {
            date: currentDate || '',
            earnings: currentPayin,
            fullData: {
              earnings: currentPayin,
              monthStartBalance: currentBalance,
              spendings: currentPayout,
            },
            monthStartBalance: null,
            spendings: currentPayout,
          },
        );
      }
    }

    const lastBalance = balance[payin.length - 1];

    if (lastBalance) {
      // Add an extra fake point at the end of the graph
      data.push({
        date: '',
        earnings: null,
        monthStartBalance: null,
        spendings: null,
      });
    }
    return data;
  }, [balance, payin, payout]);

  if (error) {
    return (
      <Center height="100%">
        <ErrorCard />
      </Center>
    );
  }

  if (!payin && !payout) {
    return (
      <ContentLayout.Root>
        <ContentLayout.Body paddingX={100} size="md">
          <Box marginBottom="space-16" marginTop="80px">
            <LoadingTreasuryGraph />
          </Box>
        </ContentLayout.Body>
      </ContentLayout.Root>
    );
  }

  return (
    <ContentLayout.Root>
      <ContentLayout.Body paddingX={100} size="md">
        <HStack justifyContent="space-between" marginBottom="space-16">
          <HStack spacing="space-8">
            <Heading size="lg">{locales.title}</Heading>

            <BetaTag showTooltip />
          </HStack>

          <a
            href={dashboardLocales.feedback.feedbackLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Button icon="link" variant="secondary">
              {dashboardLocales.feedback.feedbackCTA}
            </Button>
          </a>
        </HStack>

        <CashflowBankAccountPicker
          onBankAccountChange={(id) => {
            if (id) {
              setSelectedBankAccountId(id);
            }
          }}
          selectedBankAccountId={selectedBankAccountId}
        />

        <IntervalContextProvider granularity={granularity} interval={interval}>
          <Box marginBottom="space-16" minWidth="900px">
            <Box marginBottom="space-24">
              <RangeIntervalPicker
                onChange={setInterval}
                /**
                 * @todo once we figure out how to nicely display a lot of points in treasury graph we can put back other choices.
                 * @see https://linear.app/shine/issue/CPA-1246/quarter-view-of-the-tresorerie-graph-is-not-readable
                 */
                periodOptions={['year']}
                value={interval}
              />
            </Box>

            {needsEmptyState ? (
              <GraphEmptyState height="325px" />
            ) : (
              <>
                {needsUnavailableDateState ? (
                  <UnavailableDateEmptyState height="325px" />
                ) : (
                  <Box height="325px">
                    <BarLineGraph data={graphData} />
                  </Box>
                )}
              </>
            )}
          </Box>
        </IntervalContextProvider>
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

export default Treasury;
